import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';

interface Participant {
  userId: string;
  userName?: string;
  stream?: MediaStream;
}

interface UseWebRTCOptions {
  rehearsalId: string;
  userId: string;
  userName?: string;
  autoConnect?: boolean;
}

interface UseWebRTCReturn {
  localStream: MediaStream | null;
  participants: Map<string, Participant>;
  isConnected: boolean;
  error: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  toggleAudio: () => void;
  toggleVideo: () => void;
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
}

/**
 * Custom hook for managing WebRTC connections in rehearsal sessions
 * Implements Requirement 27.3 (peer-to-peer streams for rehearsal participants)
 */
export const useWebRTC = (options: UseWebRTCOptions): UseWebRTCReturn => {
  const { rehearsalId, userId, userName, autoConnect = false } = options;

  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [participants, setParticipants] = useState<Map<string, Participant>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);

  const socketRef = useRef<Socket | null>(null);
  const peerConnectionsRef = useRef<Map<string, RTCPeerConnection>>(new Map());
  const localStreamRef = useRef<MediaStream | null>(null);

  const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:4000';

  // ICE server configuration
  const iceServers: RTCConfiguration = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' }
    ]
  };

  /**
   * Initialize local media stream
   */
  const initializeLocalStream = async (): Promise<MediaStream> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: true
      });
      localStreamRef.current = stream;
      setLocalStream(stream);
      return stream;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to access media devices';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  /**
   * Create a peer connection for a remote user
   */
  const createPeerConnection = useCallback((remoteUserId: string): RTCPeerConnection => {
    const peerConnection = new RTCPeerConnection(iceServers);

    // Add local stream tracks to peer connection
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        peerConnection.addTrack(track, localStreamRef.current!);
      });
    }

    // Handle incoming remote stream
    peerConnection.ontrack = (event) => {
      const [remoteStream] = event.streams;
      setParticipants(prev => {
        const updated = new Map(prev);
        const participant = updated.get(remoteUserId) || { userId: remoteUserId };
        participant.stream = remoteStream;
        updated.set(remoteUserId, participant);
        return updated;
      });
    };

    // Handle ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate && socketRef.current) {
        socketRef.current.emit('rtc:ice-candidate', {
          from: userId,
          to: remoteUserId,
          candidate: event.candidate.toJSON()
        });
      }
    };

    // Handle connection state changes
    peerConnection.onconnectionstatechange = () => {
      console.log(`Peer connection state with ${remoteUserId}: ${peerConnection.connectionState}`);
    };

    peerConnectionsRef.current.set(remoteUserId, peerConnection);
    return peerConnection;
  }, [userId, iceServers]);

  /**
   * Create and send WebRTC offer to a remote peer
   */
  const createOffer = useCallback(async (remoteUserId: string) => {
    const peerConnection = createPeerConnection(remoteUserId);

    try {
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);

      if (socketRef.current) {
        socketRef.current.emit('rtc:offer', {
          from: userId,
          to: remoteUserId,
          offer: offer
        });
      }
    } catch (err) {
      console.error('Error creating offer:', err);
      setError('Failed to create WebRTC offer');
    }
  }, [userId, createPeerConnection]);

  /**
   * Handle incoming WebRTC offer
   */
  const handleOffer = useCallback(async (data: { from: string; offer: RTCSessionDescriptionInit }) => {
    const { from, offer } = data;
    const peerConnection = createPeerConnection(from);

    try {
      await peerConnection.setRemoteDescription(new RTCSessionDescription(offer));
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);

      if (socketRef.current) {
        socketRef.current.emit('rtc:answer', {
          from: userId,
          to: from,
          answer: answer
        });
      }
    } catch (err) {
      console.error('Error handling offer:', err);
      setError('Failed to handle WebRTC offer');
    }
  }, [userId, createPeerConnection]);

  /**
   * Handle incoming WebRTC answer
   */
  const handleAnswer = useCallback(async (data: { from: string; answer: RTCSessionDescriptionInit }) => {
    const { from, answer } = data;
    const peerConnection = peerConnectionsRef.current.get(from);

    if (peerConnection) {
      try {
        await peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
      } catch (err) {
        console.error('Error handling answer:', err);
        setError('Failed to handle WebRTC answer');
      }
    }
  }, []);

  /**
   * Handle incoming ICE candidate
   */
  const handleIceCandidate = useCallback(async (data: { from: string; candidate: RTCIceCandidateInit }) => {
    const { from, candidate } = data;
    const peerConnection = peerConnectionsRef.current.get(from);

    if (peerConnection) {
      try {
        await peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      } catch (err) {
        console.error('Error adding ICE candidate:', err);
      }
    }
  }, []);

  /**
   * Connect to WebRTC room
   */
  const connect = useCallback(async () => {
    try {
      // Initialize local stream
      await initializeLocalStream();

      // Connect to signaling server
      const socket = io(SOCKET_URL);
      socketRef.current = socket;

      socket.on('connect', () => {
        console.log('Connected to signaling server');
        setIsConnected(true);

        // Join WebRTC room
        socket.emit('rtc:join-room', {
          rehearsalId,
          userId,
          userName
        });
      });

      // Handle room joined - connect to existing participants
      socket.on('rtc:room-joined', (data: { participants: Array<{ userId: string; userName?: string }> }) => {
        console.log('Joined WebRTC room, existing participants:', data.participants);
        
        // Create offers for all existing participants
        data.participants.forEach(participant => {
          createOffer(participant.userId);
        });
      });

      // Handle new participant joining
      socket.on('rtc:participant-joined', (data: { userId: string; userName?: string }) => {
        console.log('New participant joined:', data.userId);
        setParticipants(prev => {
          const updated = new Map(prev);
          updated.set(data.userId, { userId: data.userId, userName: data.userName });
          return updated;
        });
      });

      // Handle participant leaving
      socket.on('rtc:participant-left', (data: { userId: string }) => {
        console.log('Participant left:', data.userId);
        
        // Close peer connection
        const peerConnection = peerConnectionsRef.current.get(data.userId);
        if (peerConnection) {
          peerConnection.close();
          peerConnectionsRef.current.delete(data.userId);
        }

        // Remove from participants
        setParticipants(prev => {
          const updated = new Map(prev);
          updated.delete(data.userId);
          return updated;
        });
      });

      // Handle WebRTC signaling events
      socket.on('rtc:offer', handleOffer);
      socket.on('rtc:answer', handleAnswer);
      socket.on('rtc:ice-candidate', handleIceCandidate);

      socket.on('disconnect', () => {
        console.log('Disconnected from signaling server');
        setIsConnected(false);
      });

      socket.on('rtc:error', (data: { message: string }) => {
        console.error('WebRTC error:', data.message);
        setError(data.message);
      });

    } catch (err) {
      console.error('Error connecting to WebRTC:', err);
      setError(err instanceof Error ? err.message : 'Failed to connect');
    }
  }, [rehearsalId, userId, userName, SOCKET_URL, createOffer, handleOffer, handleAnswer, handleIceCandidate]);

  /**
   * Disconnect from WebRTC room
   */
  const disconnect = useCallback(() => {
    // Close all peer connections
    peerConnectionsRef.current.forEach(pc => pc.close());
    peerConnectionsRef.current.clear();

    // Stop local stream
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
      setLocalStream(null);
    }

    // Disconnect socket
    if (socketRef.current) {
      socketRef.current.emit('rtc:leave-room', {
        rehearsalId,
        userId
      });
      socketRef.current.disconnect();
      socketRef.current = null;
    }

    setIsConnected(false);
    setParticipants(new Map());
  }, [rehearsalId, userId]);

  /**
   * Toggle audio track
   */
  const toggleAudio = useCallback(() => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsAudioEnabled(audioTrack.enabled);
      }
    }
  }, []);

  /**
   * Toggle video track
   */
  const toggleVideo = useCallback(() => {
    if (localStreamRef.current) {
      const videoTrack = localStreamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoEnabled(videoTrack.enabled);
      }
    }
  }, []);

  // Auto-connect if enabled
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [autoConnect]);

  return {
    localStream,
    participants,
    isConnected,
    error,
    connect,
    disconnect,
    toggleAudio,
    toggleVideo,
    isAudioEnabled,
    isVideoEnabled
  };
};
